import React, { useState, useEffect, useRef, useMemo } from "react";
import { Table, Popconfirm, Space, Dropdown, Modal, Form, Popover } from "antd";
import { Select, DatePicker, Button, Tooltip, Segmented, Tag, InputNumber, Empty, Typography, Checkbox, Carousel } from "antd";
import dayjs from "dayjs";
import { TrainingAPI } from "../../api/TrainingAPI";
import "../../Styles/styles-InjuryTable.css";
import { formattedPlayerName } from "../../Utils/name_utils";
import { useSelector, useDispatch } from "react-redux";
import { ReloadOutlined, InfoCircleOutlined, InfoCircleFilled, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import { 
  useUserPermissions,
  PermissionedButton, 
  PermissionedSelect, 
  PermissionedDatePicker, 
  PermissionedInputNumber, 
  PermissionedSegmented, 
  PermissionedPopConfirm 
} from "../PermissionedComponents";
import { usePermissionCheck } from "../PermissionedComponents/usePermissionCheck";
import PermissionViewable from "../PermissionedComponents/PermissionViewable";
import PieChart from "../Charts/PieChart";
import {PlayerOptions} from "@components/Common/PlayerOptions";
import { InjuryBulkUpload } from "./InjuryBulkUpload";

const { Option } = Select;

const INJURY_STATUS = {
  INJURED: "Injured",
  RTP: "Return to play",
  RECOVERED: "Recovered"
}

// Helper functions for pie chart data calculation
const getChartColors = (teamColors = []) => {
  const defaultColors = ['#89f5ff', '#8884d8', '#a7ff81', '#ffaea3', '#4797ff', '#f526fA'];
  const validTeamColors = teamColors.length > 0 ? teamColors.slice(0, 5) : [];
  return validTeamColors.length > 0 ? validTeamColors : defaultColors;
};


function exportToCsv(filename, rows, cols) {
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    const needsQuotes = /[",\n\r]/.test(s);
    const escaped = s.replace(/"/g, '""');
    return needsQuotes ? `"${escaped}"` : escaped;
  };

  const getValue = (row, dataIndex) => {
    if (!dataIndex) return "";
    if (Array.isArray(dataIndex)) {
      return dataIndex.reduce((acc, k) => (acc ? acc[k] : undefined), row);
    }
    return row[dataIndex];
  };

  const exportCols = (cols || []).filter((c) => c && c.title);

  const header = exportCols.map((c) => escape(c.title)).join(",");

  const body = (rows || [])
    .map((row) =>
      exportCols
        .map((c) => {
          // 1) use explicit export renderer if provided
          if (typeof c.exportRender === "function") {
            const raw = getValue(row, c.dataIndex);
            return escape(c.exportRender(raw, row));
          }

          // 2) fallback to plain value
          return escape(getValue(row, c.dataIndex));
        })
        .join(",")
    )
    .join("\n");

  const csv = `${header}\n${body}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  message.success("File downloaded successfully.");
}


const calculateInjuryTypeDistribution = (injuryData=[], teamColors = []) => {
  const typeCounts = {};

  injuryData.forEach(injury => {
    const type = injury.injury_type || 'Unknown';
    typeCounts[type] = (typeCounts[type] || 0) + 1;
  });

  const colorsToUse = getChartColors(teamColors);

  return Object.entries(typeCounts).map(([type, count], index) => ({
    name: type,
    y: count,
    color: colorsToUse[index % colorsToUse.length]
  }));
};

const calculateInjuryLocationDistribution = (injuryData =[], teamColors = []) => {
  const locationCounts = {};

  injuryData.forEach(injury => {
    const location = injury.injury_location || 'Unknown';
    locationCounts[location] = (locationCounts[location] || 0) + 1;
  });

  const colorsToUse = getChartColors(teamColors);

  return Object.entries(locationCounts).map(([location, count], index) => ({
    name: location,
    y: count,
    color: colorsToUse[index % colorsToUse.length]
  }));
};

const calculateInjuryStatusDistribution = (injuryData=[], teamColors = []) => {
  const statusCounts = {};

  injuryData.forEach(injury => {
    const status = injury.status || 'Unknown';
    statusCounts[status] = (statusCounts[status] || 0) + 1;
  });

  const colorsToUse = getChartColors(teamColors);

  return Object.entries(statusCounts).map(([status, count], index) => ({
    name: status,
    y: count,
    color: colorsToUse[index % colorsToUse.length]
  }));
};

const calculateActivityDistribution = (injuryData=[], teamColors = []) => {
  const activityCounts = {};

  injuryData.forEach(injury => {
    const activity = injury.activity || 'Unknown';
    activityCounts[activity] = (activityCounts[activity] || 0) + 1;
  });

  const colorsToUse = getChartColors(teamColors);

  return Object.entries(activityCounts).map(([activity, count], index) => ({
    name: activity,
    y: count,
    color: colorsToUse[index % colorsToUse.length]
  }));
};

const InjuryInputForm = (props) => {

  const [playerId, setPlayerId] = useState(undefined);
  const [injuryLocation, setInjuryLocation] = useState(undefined);
  const [injuryType, setInjuryType] = useState(undefined);
  const [softTissue, setSoftTissue] = useState('N/A');
  const [surgery, setSurgery] = useState('N/A');
  const [referral, setReferral] = useState('N/A');
  const [image, setImage] = useState('N/A');
  const [seasonEnding, setSeasonEnding] = useState('N/A');
  const [activity, setActivity] = useState(undefined);
  const [drill, setDrill] = useState(undefined);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [drillList, setDrillList] = useState([]);

  const trainingAPI = new TrainingAPI();


  useEffect(() => {
    trainingAPI.getDrills().then((data) => setDrillList(data)).catch((error) => console.log(error));
  }, [])

  const userPermissions = useUserPermissions();
  const {
    misc: {
      injury_types: injury_type_list, 
      injury_location_tree,
      injury_activity_list
    },
    players,
    info: { display_name: teamName},
    playersStatus
  } = useSelector(({ team }) => team);

  const modifiedInjuryLocationTree = injury_location_tree.map(
    (item) => ({...item, onTitleClick: () => setInjuryLocation(item.key)}));

  const modified_drill_list = drillList.concat([
    {name: "Non Sport Related"},
    {name: "Pre Practice"},
    {name: "Post Practice"},
    {name: "Other"},
    {name: "N/A"},
  ])

  const onSubmit = () => {
    const missingFields = [
      !selectedDate && "Date",
      !playerId && "Name",
      !injuryType && "Injury Type",
    ].filter(Boolean);
    if (missingFields.length > 0) {
      message.error(`Please fill in: ${missingFields.join(", ")}`);
      return;
    }

    const payload = {
      player_id: playerId,
      injury_location: injuryLocation,
      injury_type: injuryType,
      is_soft_tissue: softTissue,
      is_surgery: surgery,
      is_referral: referral,
      is_image: image,
      is_season_ending: seasonEnding,
      activity: activity,
      drill: drill,
      injury_start_date: selectedDate
    }

    trainingAPI.newInjuryV2(payload).then((data) => {
        const player = players.find((e) => e.player_id === playerId);
        message.success(`${player.first_name} ${player.last_name} injury uploaded successfully.`);
    }).catch((error) => {
      if (error?.response?.status === 400 && error?.response?.data) {
        message.error(error.response.data);
        return
      } else {
        message.error("Error uploading injury");
      }
    })
    props?.setIsFormOpen(false);
  }

  return (
  <>

        <Form
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 20 }}
        layout="horizontal"
        style={{ maxWidth: 600 }}
      >

        <Form.Item 
          name="Date" 
          label="Date" 
          rules={[{ required: true }]} 
          validateDebounce={500}
          >
          <PermissionedDatePicker
            permissionCheck="AddPlayerInjuries"
            onChange={(date, dateString) => setSelectedDate(dateString)}
            format={"YYYY-MM-DD"}
            value={selectedDate}
          />
        </Form.Item>

        <Form.Item 
         name="Name" 
         label="Name" 
         rules={[{ required: true }]} 
         validateDebounce={500}
          >
        <PermissionedSelect
            permissionCheck="AddPlayerInjuries"
            showSearch
            allowClear
            style={{ width: 250 }}
            placeholder="Choose Player"
            value={name}
            onChange={(value) => {setPlayerId(value)}}
            filterOption={(input, option) =>
              option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {players !== null ? (
              players.map((player, i) => {
                return PlayerOptions({ player, teamName, index: i, playersStatus });
              })
            ) : (
              <></>
            )}
          </PermissionedSelect>
        </Form.Item>

        <Form.Item 
          name="Injury Type" 
          label="Injury Type" 
          rules={[{ required: true }]} 
          validateDebounce={500}
          >
        <PermissionedSelect
            permissionCheck="AddPlayerInjuries"
            showSearch
            allowClear={true}
            placeholder="Choose Injury Type"
            value={injuryType}
            style={{ width: 250 }}
            onChange={(value) => setInjuryType(value)}
            options={injury_type_list.map(x => ({'value': x, 'label': x}))}
          />
        </Form.Item>

        <Form.Item label="Soft Tissue">
        <PermissionedSegmented
          permissionCheck="AddPlayerInjuries"
          options={['Yes', 'No', 'N/A']}
          value={softTissue}
          onChange={(value) => setSoftTissue(value)}
        />
        </Form.Item>

        <Form.Item label="Injury Location" rules={[{ required: true }]} validateDebounce={500}>
        <Dropdown
					menu={{
						items: modifiedInjuryLocationTree,
						onClick: ({ key }) => {setInjuryLocation(key);},
					}}
          style={{ 
            width: 250,
            
           }}
           overlayStyle={{
            // maxHeight: '300px',
            // overflow: 'hidden', 
          }}
					trigger={["hover"]}
				>
          <Button
          style={{
            height: "auto",
            whiteSpace: "wrap",
            width: 250
          }}
          variant="outlined"
        >
            {injuryLocation ? injuryLocation : "Choose Injury Location"}
          </Button>
        </Dropdown>

        </Form.Item>

        <Form.Item label="Surgery">
          <PermissionedSegmented
            permissionCheck="AddPlayerInjuries"
            options={['Yes', 'No', 'N/A']}
            value={surgery}
            onChange={(value) => setSurgery(value)}
          />
        </Form.Item>

        <Form.Item label="Referral">
          <PermissionedSegmented
            permissionCheck="AddPlayerInjuries"
            options={['Yes', 'No', 'N/A']}
            value={referral}
            onChange={(value) => setReferral(value)}
          />
        </Form.Item>
          
        <Form.Item label="Image">
          <PermissionedSegmented
            permissionCheck="AddPlayerInjuries"
            options={['Yes', 'No', 'N/A']}
            value={image}
            onChange={(value) => setImage(value)}
          />
        </Form.Item>

        <Form.Item label="Season Ending">
          <PermissionedSegmented
            permissionCheck="AddPlayerInjuries"
            options={['Yes', 'No', 'N/A']}
            value={seasonEnding}
            onChange={(value) => setSeasonEnding(value)}
          />
        </Form.Item>

        <Form.Item label="Activity">
          <PermissionedSelect
            permissionCheck="AddPlayerInjuries"
            allowClear={true}
            showSearch
            placeholder="Choose Activity"
            value={activity}
            style={{ width: 250 }}
            onChange={(value) => setActivity(value)}
            options={injury_activity_list.map(x => ({'value': x, 'label': x}))}
          />
        </Form.Item>

        <Form.Item label="Drill">
          <PermissionedSelect
            permissionCheck="AddPlayerInjuries"
            allowClear={true}
            showSearch
            placeholder="Drill"
            value={drill}
            style={{ width: 250 }}
            onChange={(value) => setDrill(value)}
            options={modified_drill_list.map(x => ({'value': x.name, 'label': x.name}))}
          />
        </Form.Item>
        <Form.Item label={null}>
          <PermissionedButton 
            type="primary" 
            permissionCheck="AddPlayerInjuries"
            onClick={onSubmit}
          >
            Report Injury
          </PermissionedButton>
        </Form.Item>
      </Form>
  </>
  )

}

export const InjuryTable = (props) => {
  const [tableData, setTableData] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showColumns, setShowColumns] = useState(["Name", "Injury Type", "Injury Location", "Injury Start Date", "Recovery Start Date", "Injury End Date", "Duration (Days)", "Status"]);
  const [showChartLabels, setShowChartLabels] = useState(true);
  const [currentCarouselSlide, setCurrentCarouselSlide] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [currentTableFilters, setCurrentTableFilters] = useState({});
  const carouselRef = useRef(null);
  const extraColumns = [];

  // Filter states
  const dateFormat = "YYYY-MM-DD";
  // The API without a date range returns only open injuries and injuries that
  // ended in the last seven days, so the page always sends a range. The default
  // window is the last twelve months.
  const defaultDateRange = () => [
    dayjs().subtract(12, "month").format(dateFormat),
    dayjs().format(dateFormat),
  ];
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [filters, setFilters] = useState({
    player: null,
    positions: [],
    groups: []
  });

  const trainingAPI = new TrainingAPI();

  // Use permission check hook
  const { permissionDisabled } = usePermissionCheck("ViewPlayerInjuries");

  // Get data from Redux
  const userPermissions = useUserPermissions();
  const {
    misc: {
      injury_types: injury_type_list, 
      injury_location_tree,
      injury_activity_list
    },
    players,
    positions,
    playerGroups,
    info,
    info: { display_name: teamName},
    playersStatus
  } = useSelector(({ team }) => team);

  useEffect(() => {
    getInjuries();
  }, [isFormOpen, permissionDisabled]);

  // Helper function to apply table filters to data
  const applyTableFilters = (data, filters) => {
    if (!filters || Object.keys(filters).length === 0) {
      return data;
    }
    
    return data.filter(record => {
      return Object.entries(filters).every(([key, filterValues]) => {
        if (!filterValues || filterValues.length === 0) return true;
        return filterValues.includes(record[key]);
      });
    });
  };

  // Update both filtered data and pie chart data when table data or table filters change
  useEffect(() => {
    const newFilteredData = applyTableFilters(tableData, currentTableFilters);
    setFilteredData(newFilteredData);
    setPieChartData(newFilteredData);
  }, [tableData, currentTableFilters]);

  // Memoize chart options to prevent unnecessary re-renders and animations
  const injuryTypeChartOptions = useMemo(() => ({
    title: {
      text: 'Injury Type',
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
      }
    },
    series: [{
      name: 'Count',
      data: calculateInjuryTypeDistribution(pieChartData, info?.team_colors?.all || [])
    }],
    tooltip: {
      pointFormat: '{series.name}: <b>{point.y}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>'
    }
  }), [pieChartData, info?.team_colors?.all, showChartLabels]);

  const injuryLocationChartOptions = useMemo(() => ({
    title: {
      text: 'Injury Location',
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
      }
    },
    series: [{
      name: 'Count',
      data: calculateInjuryLocationDistribution(pieChartData, info?.team_colors?.all || [])
    }],
    tooltip: {
      pointFormat: '{series.name}: <b>{point.y}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>'
    }
  }), [pieChartData, info?.team_colors?.all, showChartLabels]);

  const injuryStatusChartOptions = useMemo(() => ({
    title: {
      text: 'Injury Status',
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
      }
    },
    series: [{
      name: 'Count',
      data: calculateInjuryStatusDistribution(pieChartData, info?.team_colors?.all || [])
    }],
    tooltip: {
      pointFormat: '{series.name}: <b>{point.y}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>'
    }
  }), [pieChartData, info?.team_colors?.all, showChartLabels]);

  const activityChartOptions = useMemo(() => ({
    title: {
      text: 'Activity',
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
      }
    },
    series: [{
      name: 'Count',
      data: calculateActivityDistribution(pieChartData, info?.team_colors?.all || [])
    }],
    tooltip: {
      pointFormat: '{series.name}: <b>{point.y}</b><br/>Percentage: <b>{point.percentage:.1f}%</b>'
    }
  }), [pieChartData, info?.team_colors?.all, showChartLabels]);

  const fetchInjuries = (apiFilters) => {
    if (permissionDisabled) return;
    trainingAPI.getInjuries(apiFilters)
      .then((data) => {
        const tableDataWithStatus = data.map( (injury) => ({...injury, status: getInjuryStatus(injury?.recovery_start_date, injury?.injury_end_date)}))
        setTableData(tableDataWithStatus)
      })
      .catch(() => message.error("Error loading injuries"));
  };

  const getInjuries = (filterParams = {}) => {
    // Prepare filter parameters for API request
    const apiFilters = {
      ...filterParams,
      start_date: dateRange[0],
      end_date: dateRange[1]
    };

    // Remove null/empty values
    Object.keys(apiFilters).forEach(key => {
      if (!apiFilters[key] || (Array.isArray(apiFilters[key]) && apiFilters[key].length === 0)) {
        delete apiFilters[key];
      }
    });
    fetchInjuries(apiFilters);
  }

  const handleApplyFilters = () => {
    const filterParams = {
      player: filters.player,
      positions: filters.positions,
      groups: filters.groups
    };
    
    getInjuries(filterParams);
    message.success("Filters applied");
  };

  const handleClearFilters = () => {
    const resetRange = defaultDateRange();
    setDateRange(resetRange);
    setFilters({
      player: null,
      positions: [],
      groups: []
    });
    
    // Clear table filters as well
    setCurrentTableFilters({});
    
    const clearedFilters = {
      player: null,
      positions: [],
      groups: []
    };
    const apiFilters = {
      ...clearedFilters,
      start_date: resetRange[0],
      end_date: resetRange[1]
    };
    Object.keys(apiFilters).forEach(key => {
      if (!apiFilters[key] || (Array.isArray(apiFilters[key]) && apiFilters[key].length === 0)) {
        delete apiFilters[key];
      }
    });
    
    fetchInjuries(apiFilters);
    message.success("Filters cleared");
  };

  const saveTable = () => {
    trainingAPI.updateInjury(tableData).then((data) => {
      message.success("Injury Info Updated");
      // Reset save flags after successful save
      const resetTableData = tableData.map((item) => {
        const { save_recover, save_end, ...rest } = item;
        return rest;
      });
      setTableData(resetTableData);
      getInjuries()
    });
  };

  const onRecoveryDateChange = (value, e, dateString) => {
    const newTableData = tableData.map((data) => {
      if (value === data["injury_id"]) {
        if (dayjs(data["injury_start_date"]) > dayjs(dateString)) {
          message.error(
            "Recovery Start Date must be on or after Injury Start Date"
          );
          return data;
        }
        return {
          ...data,
          recovery_start_date: dateString,
          save_recover: false,
        };
      } else {
        return data;
      }
    });
    setTableData([...newTableData]);
  };

  const onInjuryEndDateChange = (value, e, dateString) => {
    const newTableData = tableData.map((data) => {
      if (value === data["injury_id"]) {
        if (dayjs(data["recovery_start_date"]) > dayjs(dateString)) {
          message.error(
            "Injury End Date must be on or after Recovery Start Date"
          );
          return data;
        }
        data["injury_end_date"] = dateString;
        data["save_end"] = false;
        return { ...data };
      } else {
        return data;
      }
    });
    setTableData([...newTableData]);
  };

  const handleGameCountChange = (injury_id, value) => {
    const newTableData = tableData.map((data) => {
      if (injury_id == data["injury_id"]) {
        data["games_lost"] = value;
        return { ...data };
      } else {
        return data;
      }
    });
    setTableData([...newTableData]);
  }
  const handleDelete = async () => {

    const del_all = selectedRowKeys.map((injuryId) => {
      trainingAPI.deleteInjury(injuryId).then(() => {}).catch((data) => {console.log(data);})
    })

    const fin = await Promise.all(del_all);
    message.success("Injury data deleted successfully.");
    getInjuries();
  };

  const getInjuryStatus = (recoveryStartDate, injuryEndDate) => {
    if(injuryEndDate) {
      return INJURY_STATUS.RECOVERED
    }

    if(recoveryStartDate) {
      return INJURY_STATUS.RTP
    }

    return INJURY_STATUS.INJURED
  }

  const generateFilters = (data, key) => {
    return [
      ...new Set(data.map((item) => item[key])),
    ].map((value) => ({ text: value, value }));
  };

  const handleColumnChange = (column) => {
    if (showColumns.includes(column)) {
      setShowColumns(showColumns.filter((col) => col !== column));
    } else {
      setShowColumns([...showColumns, column]);
   }
  }

  const utc_date_string_to_local = (utc_date_string) => {
    return dayjs.utc(utc_date_string).tz("America/Los_Angeles").format(dateFormat);
  }

  const getFormattedMetadata = (data) => {

    const raw_records = data?.metadata?.raw || [];
    const timeline = data?.metadata?.timeline || [];

    return (
      <div className="metadata-container">
        Name: <b>{raw_records[0].player_name}</b><br />
        Injury ID: <b>{raw_records[0].injury_id}</b><br />
        Body Area: <b>{raw_records[0].clinical_impression_body_area}</b><br />
        Classification: <b>{raw_records[0].clinical_impression_classification}</b><br />
        Contact Type: <b>{raw_records[0].contact_type}</b><br />
        Injury Type: <b>{raw_records[0].injury_type}</b><br />
        Examination: <b>{utc_date_string_to_local(raw_records[0].injury_examination_date)}</b><br />
        Event Status Log:
        <hr style={{ margin: 3 }} />
        <div>
          {timeline.map(dp => (
            <div>
              {dp.start_date}: <span style={{color: dp.burden ? '#ff4d4f': '#000000'}}><b>{dp.status}</b></span>
            </div>
          ))}
        </div>
      </div>
    )
  }

    if (teamName === "Las Vegas Raiders") {
    extraColumns.push({
      title: "Burden",
      dataIndex: "burden",
      key: "burden",
      sorter: {
        compare: (a, b) => a.burden - b.burden,
        multiple: 4,
      },
      exportRender: (_, data) => data["burden"],
    })
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      fixed: 'left',
      width: 180,
      sorter: {
        compare: ((a, b) => {
          const a_fullname = `${a["player_id__first_name"]} ${a["player_id__last_name"]}`;
          const b_fullname = `${b["player_id__first_name"]} ${b["player_id__last_name"]}`;
          return a_fullname.localeCompare(b_fullname);
        }),
        multiple: 1,
      },
      render: (_, data) => (
        <span>
          {formattedPlayerName(
            props.team_name,
            `${data["player_id__first_name"]} ${data["player_id__last_name"]}`
          )}
          {(data?.metadata?.raw || []).length > 0 ? (
          <Popover
            title="Kitman Labs Info"
            content={getFormattedMetadata(data)}
            trigger={"click"}
            placement="top"
            >
            <Button style={{float: 'right'}} size="small" type="text" icon={<InfoCircleFilled style={{fontSize: 16, color: '#ccc'}} />} />
          </Popover>
          ) : null}
        </span>
      ),
      exportRender: (_, data) => `${formattedPlayerName(
            props.team_name,
            `${data["player_id__first_name"]} ${data["player_id__last_name"]}`
          )}`,
      },
    {
      title: "Position",
      dataIndex: "player_id__position",
      key: "player_id__position",
      sorter: {
        compare: (a, b) => a.player_id__position.localeCompare(b.player_id__position),
        multiple: 1,
      },
      render: (data) => <span>{data}</span>,
      filters: generateFilters(tableData, "player_id__position"),
      onFilter: (value, record) => record["player_id__position"] === value,
      exportRender: (_, data) => data["player_id__position"],
    },
    {
      title: "Injury Type",
      dataIndex: "injury_type",
      key: "injury_type",
      sorter: {
        compare: (a, b) => a.injury_type.localeCompare(b.injury_type),
        multiple: 2,
      },
      render: (injuryType) => <span>{injuryType}</span>,
      filters: generateFilters(tableData, "injury_type"),
      onFilter: (value, record) => record["injury_type"] === value,
      exportRender: (_, data) => data["injury_type"],
    },
    {
      title: "Soft Tissue",
      dataIndex: "is_soft_tissue",
      key: "is_soft_tissue",
      sorter: {
        compare: (a, b) => a.is_soft_tissue.localeCompare(b.is_soft_tissue),
        multiple: 2,
      },
      render: (is_soft_tissue) => <span>{is_soft_tissue}</span>,
      filters: generateFilters(tableData, "is_soft_tissue"),
      onFilter: (value, record) => record["is_soft_tissue"] === value,
      exportRender: (_, data) => data["is_soft_tissue"],
    },
    {
      title: "Injury Location",
      dataIndex: "injury_location",
      key: "injury_location",
      sorter: {
        compare: (a, b) => a.injury_location.localeCompare(b.injury_location),
        multiple: 2,
      },
      render: (injuryLocation) => <span>{injuryLocation}</span>,
      filters: generateFilters(tableData, "injury_location"),
      onFilter: (value, record) => record["injury_location"] === value,
      exportRender: (_, data) => data["injury_location"],
    },
    {
      title: "Surgery",
      dataIndex: "is_surgery",
      key: "is_surgery",
      sorter: {
        compare: (a, b) => a.is_surgery.localeCompare(b.is_surgery),
        multiple: 2,
      },
      render: (is_surgery) => <span>{is_surgery}</span>,
      filters: generateFilters(tableData, "is_surgery"),
      onFilter: (value, record) => record["is_surgery"] === value,
      exportRender: (_, data) => data["is_surgery"],
    },

    {
      title: "Referral",
      dataIndex: "is_referral",
      key: "is_referral",
      sorter: {
        compare: (a, b) => a.is_referral.localeCompare(b.is_referral),
        multiple: 2,
      },
      render: (is_referral) => <span>{is_referral}</span>,
      filters: generateFilters(tableData, "is_referral"),
      onFilter: (value, record) => record["is_referral"] === value,
      exportRender: (_, data) => data["is_referral"],
    },

    {
      title: "Image",
      dataIndex: "is_image",
      key: "is_image",
      sorter: {
        compare: (a, b) => a.is_image.localeCompare(b.is_image),
        multiple: 2,
      },
      render: (is_image) => <span>{is_image}</span>,
      filters: generateFilters(tableData, "is_image"),
      onFilter: (value, record) => record["is_image"] === value,
      exportRender: (_, data) => data["is_image"],
    },

    {
      title: "Season Ending",
      dataIndex: "is_season_ending",
      key: "is_season_ending",
      sorter: {
        compare: (a, b) => a.is_season_ending.localeCompare(b.is_season_ending),
        multiple: 2,
      },
      render: (is_season_ending) => <span>{is_season_ending}</span>,
      filters: generateFilters(tableData, "is_season_ending"),
      onFilter: (value, record) => record["is_season_ending"] === value,
      exportRender: (_, data) => data["is_season_ending"],
    },

    {
      title: "Activity",
      dataIndex: "activity",
      key: "activity",
      sorter: {
        compare: (a, b) => a.activity.localeCompare(b.activity),
        multiple: 2,
      },
      render: (activity) => <span>{activity}</span>,
      filters: generateFilters(tableData, "activity"),
      onFilter: (value, record) => record["activity"] === value,
      exportRender: (_, data) => data["activity"],
    },

    {
      title: "Drill",
      dataIndex: "drill",
      key: "drill",
      sorter: {
        compare: (a, b) => a.drill.localeCompare(b.drill),
        multiple: 2,
      },
      render: (drill) => <span>{drill}</span>,
      filters: generateFilters(tableData, "drill"),
      onFilter: (value, record) => record["drill"] === value,
      exportRender: (_, data) => data["drill"],
    },
    {
      title: "Injury Start Date",
      dataIndex: "injury_start_date",
      key: "injury_start_date",
      sorter: {
        compare: (a, b) => (a.injury_start_date || "").localeCompare(b.injury_start_date || ""),
        multiple: 3,
      },
      render: (injuryStartDate) => <span>{injuryStartDate}</span>,
      filters: generateFilters(tableData, "injury_start_date"),
      onFilter: (value, record) => record["injury_start_date"] === value,
      exportRender: (_, data) => data["injury_start_date"],
    },
    {
      title: "Recovery Start Date",
      dataIndex: "recovery_start_date",
      key: "recovery_start_date",
      sorter: {
        compare: (a, b) => (a.recovery_start_date || "").localeCompare(b.recovery_start_date || ""),
        multiple: 4,
      },
      filters: generateFilters(tableData, "recovery_start_date"),
      onFilter: (value, record) => record["recovery_start_date"] === value,
      render: (recoveryStartDate, data) => {
        if (recoveryStartDate !== null && (data["save_recover"] === undefined || data["save_recover"] !== false)) {
          return <span>{recoveryStartDate}</span>;
        } else {
          return (
            <PermissionedDatePicker
              permissionCheck="EditPlayerInjuries"
              key={data["injury_id"]}
              placeholder="Select Date"
              onChange={(e, other) =>
                onRecoveryDateChange(data["injury_id"], e, other)
              }
              value={
                recoveryStartDate
                  ? dayjs(recoveryStartDate)
                  : null
              }
            />
          );
        }
      },
      exportRender: (_, data) => data["recovery_start_date"],
    },
    {
      title: "Injury End Date",
      dataIndex: "injury_end_date",
      key: "injury_end_date",
      sorter: {
        compare: (a, b) => (a.injury_end_date || "").localeCompare(b.injury_end_date || ""),
        multiple: 4,
      },
      filters: generateFilters(tableData, "injury_end_date"),
      onFilter: (value, record) => record["injury_end_date"] === value,
      render: (injuryEndDate, data) => {
        if (injuryEndDate !== null && (data["save_end"] === undefined || data["save_end"] !== false)) {
          return <span>{injuryEndDate}</span>;
        } else {
          return (
            <PermissionedDatePicker
              permissionCheck="EditPlayerInjuries"
              key={data["injury_id"]}
              placeholder="Select Date"
              onChange={(e, other) =>
                onInjuryEndDateChange(data["injury_id"], e, other)
              }
              value={
                injuryEndDate
                  ? dayjs(injuryEndDate)
                  : null
              }
            />
          );
        }
      },
      exportRender: (_, data) => data["injury_end_date"],
    },

    {
      title: "Duration (Days)",
      dataIndex: "duration",
      key: "duration",
      sorter: {
        compare: (a, b) => {
          const a_start = dayjs(a["injury_start_date"]);
          const a_end = a['injury_end_date'] ? dayjs(a["injury_end_date"]) : dayjs();

          const b_start = dayjs(b["injury_start_date"]);
          const b_end = b['injury_end_date'] ? dayjs(b["injury_end_date"]) : dayjs();

          // Calculate duration in days
          const a_duration = a_end.diff(a_start, 'days');
          const b_duration = b_end.diff(b_start, 'days');

          return a_duration - b_duration;
        },
        multiple: 2,
      },
      render: (_, data) => {
        // if (data['injury_end_date']) {
          const end_date = data["injury_end_date"] ? dayjs(data["injury_end_date"]) : dayjs();
          const start_date = dayjs(data["injury_start_date"]);
          const duration = end_date.diff(start_date, 'days');
          return (<span>{duration}</span>)

        // }
        return (<span>-</span>)
      },
      exportRender: (_, data) => {
        const end_date = data["injury_end_date"] ? dayjs(data["injury_end_date"]) : dayjs();
        const start_date = dayjs(data["injury_start_date"]);
        const duration = end_date.diff(start_date, 'days');
        return duration;
      }
    },

    {
      title: "Games Lost",
      dataIndex: "games_lost",
      key: "games_lost",
      sorter: {
        compare: (a, b) => a.games_lost - b.games_lost,
        multiple: 4,
      },
      filters: generateFilters(tableData, "games_lost"),
      onFilter: (value, record) => record["games_lost"] === value,
      render: (games_lost, data) => {
        if ((data['injury_end_date'] !== null && data["save_end"] === undefined) && teamName !== "Las Vegas Raiders") {
          return <span>{games_lost}</span>;
        } else {
          return (
            <PermissionedInputNumber 
              permissionCheck="EditPlayerInjuries"
              min={0} 
              max={100000} 
              value={games_lost} 
              onChange={(val)=>handleGameCountChange(data['injury_id'], val)} 
            />
          );
        }
      },
      exportRender: (_, data) => data["games_lost"],
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: {
        compare: (a, b) => a.status.localeCompare(b.status),
        multiple: 2,
      },
      render: (status) => <span>{status}</span>,
      onFilter: (value, record) => record["status"] === value,
      filters: ["Injured", "Return to play", "Recovered"].map((value) => ({ text: value, value: value })),
      exportRender: (_, data) => data["status"],

    }
  ].concat(extraColumns);

  return (
    <div
      style={{
        margin: "0px 0px 24px",
      }}
    >
      <div style={{ display: "flex", gap: 16 }}>

      <PermissionedButton 
        type="primary"
        permissionCheck="AddPlayerInjuries"
        onClick={() => setIsFormOpen(true)}
      >
        Report Injury
      </PermissionedButton>
      <InjuryBulkUpload onSaved={() => getInjuries()} />
      <Button
        variant="outlined"
        onClick={() => {getInjuries(); message.success("Injuries refreshed")}}
        style={{right: 0, position: 'absolute'}}
        icon={<ReloadOutlined />}
        />
      </div>

      <Modal 
        title="New Injury" 
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onCancel={() => setIsFormOpen(false)}
        destroyOnHidden={true}
        footer={null}
        >
          <InjuryInputForm setIsFormOpen={setIsFormOpen}/>
        </Modal>

        <div className="health-filter-bar">
          <Typography.Text className="health-filter-bar-label">Filters:</Typography.Text>

          <DatePicker.RangePicker
            style={{ borderRadius: "5px", width: 250 }}
            format={dateFormat}
            value={[
              dateRange[0] ? dayjs(dateRange[0]) : null,
              dateRange[1] ? dayjs(dateRange[1]) : null,
            ]}
            allowClear={false}
            onChange={(dates, dateStrings) => setDateRange(dateStrings)}
            placeholder={['Start Date', 'End Date']}
          />
          
          <Select
            showSearch
            allowClear
            style={{ width: 220, flexShrink: 0 }}
            placeholder="Choose Player"
            onChange={(value) => {setFilters({...filters, player: value})}}
            value={filters?.player || null}
            filterOption={(input, option) =>
                option?.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {players?.filter(item => item.label !== "Unknown")
             .map((player, i) => {
                return PlayerOptions({ player, teamName, index: i, playersStatus });
              })
             }    
          </Select>

          <Select
            showSearch
            allowClear
            placeholder="All Positions"
            style={{ width: 220, flexShrink: 0 }}
            options={positions.map((position) => ({
              label: position,
              value: position,
            }))}
            onChange={(value) => {setFilters({...filters, positions: value})}}   
            mode="multiple"
            maxTagCount="responsive"
            maxTagTextLength={8}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            value={filters?.positions || []}
          />

          <Select
            showSearch
            allowClear
            placeholder="All Groups"
            style={{ width: 220, flexShrink: 0 }}
            options={playerGroups.map((group) => ({
              label: group.name,
              value: group.player_group_id,
            }))}
            onChange={(value) => {setFilters({...filters, groups: value})}}   
            mode="multiple"
            maxTagCount="responsive"
            maxTagTextLength={10}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            value={filters?.groups || []}
          />

          <div style={{ display: "flex", gap: "6px", flexShrink: 0, marginLeft: "auto" }}>
            <Button 
              type="primary" 
              onClick={handleApplyFilters}
              style={{ borderRadius: "5px" }}
            >
              Apply
            </Button>
            <Button 
              onClick={handleClearFilters}
              style={{ borderRadius: "5px" }}
            >
              Clear
            </Button>
          </div>
        </div>

        <PermissionViewable 
          permissionCheck="ViewPlayerInjuries"
        >
          {/* Pie Charts - Carousel showing 3 charts at a time */}
          <div style={{ position: 'relative', margin: '20px 0' }}>
            {/* Custom Navigation Arrows */}
            <Button
              className="carousel-nav-btn carousel-prev-btn"
              shape="circle"
              icon={<LeftOutlined />}
              onClick={() => carouselRef.current?.prev()}
              style={{
                position: 'absolute',
                left: '-5px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                width: '40px',
                height: '40px',
                background: 'rgba(0, 0, 0, 0.15)',
                borderColor: 'transparent',
                color: 'white',
                cursor: 'pointer'
              }}
            />
            <Button
              className="carousel-nav-btn carousel-next-btn"
              shape="circle"
              icon={<RightOutlined />}
              onClick={() => carouselRef.current?.next()}
              style={{
                position: 'absolute',
                right: '-5px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                width: '40px',
                height: '40px',
                background: 'rgba(0, 0, 0, 0.15)',
                borderColor: 'transparent',
                color: 'white',
                cursor: 'pointer'
              }}
            />
            
            <Carousel 
              ref={carouselRef}
              dots={false}
              infinite={true}
              slidesToShow={3}
              beforeChange={(from, to) => setCurrentCarouselSlide(to)}
              className="injury-charts-carousel"
            >
              {[injuryTypeChartOptions, injuryLocationChartOptions, injuryStatusChartOptions, activityChartOptions].map((options, i) => (
                <div key={i} className="injury-chart-slide">
                  <PieChart
                    showDataLabelLines={showChartLabels}
                    showDataLabels={showChartLabels}
                    showLegend={false}
                    size={showChartLabels ? '70%' : '95%'}
                    options={options}
                    height="300px"
                  />
                </div>
              ))}
            </Carousel>
          </div>
          
          {/* Chart Labels Control */}
          <div style={{ margin: '10px 0 16px 0', textAlign: 'center' }}>
            <Checkbox 
              checked={showChartLabels}
              onChange={(e) => setShowChartLabels(e.target.checked)}
              style={{ 
                color: '#888888',
                '--ant-primary-color': '#888888'
              }}
            >
              <span style={{ color: '#888888' }}>Show Chart Labels</span>
            </Checkbox>
          </div>
        </PermissionViewable>
      
        <div style={{
          fontSize: 12,
          marginTop: 16,
          backgroundColor: '#E5F3FD',
          padding: '5px',
          borderRadius: '5px',
          textAlign: 'left',
        }}>
          <b>Show Columns: </b>
          <span style={{marginLeft: '5px'}}>
            {columns.map((column) => (
              <Tag.CheckableTag 
                style={{margin: '2px'}}
                key={column.title} 
                checked={showColumns.includes(column.title)} 
                onChange={() => handleColumnChange(column.title)}
              >
                {column.title}
              </Tag.CheckableTag>))
              }
              </span>
        </div>

        <Table
          className="custom-table-container health-compact-table"
          rowClassName={() => "custom-table-row"} // Add class to each row
          columns={columns.filter((column) => showColumns.includes(column.title))}
          scroll={{ x: 'max-content', y: 1000 }}
          size="small"
          dataSource={tableData}
          bordered
          rowKey={(record) => record.injury_id}
          style={{ marginTop: 16 }}
          pagination={{
            defaultPageSize: 25,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            position: ["bottomRight"],
          }}
          locale={permissionDisabled ? {emptyText: <Empty description="You do not have permission to view the Injury Table." />}: null}
          loading={props.loading}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
          }}
          onChange={(pagination, filters, sorter, extra) => {
            // When table filters change, update the current table filters (pie charts will update via useEffect)
            if (extra.action === 'filter') {
              setCurrentTableFilters(filters);
            }
          }}
        />
      <div className="health-table-actions">
        <PermissionedButton
          type="primary"
          permissionCheck="EditPlayerInjuries"
          onClick={saveTable}
        >
          Save
        </PermissionedButton>

        <PermissionedPopConfirm
          permissionCheck="DeletePlayerInjuries"
          title="Are you sure you want to delete the selected injuries?"
          onConfirm={async () => {await handleDelete();getInjuries()}}
          okText="Yes"
          cancelText="No"
        >
          <PermissionedButton
            danger
            permissionCheck="DeletePlayerInjuries"
            disabled={selectedRowKeys.length == 0}
          >
            Delete Selected Injuries
          </PermissionedButton>
        </PermissionedPopConfirm>

        {
          teamName === "Las Vegas Raiders" ? (
        <Button
          type="primary"
          onClick={() => exportToCsv("injuries.csv", applyTableFilters(tableData, currentTableFilters), columns.filter((column) => showColumns.includes(column.title)))}
        >
          Download as CSV
        </Button>
          ) : null
        }
      </div>

      <div className="health-bottom-spacer" />
    </div>
  );
};
